import { Camera, Loader2, RefreshCw, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  FaceMatchPrepareScreen,
  FaceMatchScreen,
} from '@/features/workflows/components/simple/preview-screens';
import { toast } from '@/lib/notify';

import { useVerifyStep } from '../../api/useVerifyStep';
import type {
  SessionDetailDto,
  SessionStepApiBindingDto,
  SessionStepDto,
} from '../../schemas/sessionSchemas';
import { extractReason } from '../pan/pan.utils';
import { isSessionExpiredError, SessionExpiredScreen } from '../SessionExpiredScreen';
import {
  CHECK_FAILED_TOAST,
  RETRY_EXHAUSTED_NOTE,
  tryAnotherMethodNote,
} from '../stepFallbackNotes';
import { useCameraCapture } from '../useCameraCapture';
import { FaceDevSimulator } from './FaceDevSimulator';

// Fallback binding for legacy sessions whose snapshot carries no bindings —
// submits without a catalogueEntryId, letting the backend run its own chain.
const DEFAULT_BINDING: SessionStepApiBindingDto = {
  catalogueEntryId: '',
  path: '/api/v1/FaceMatch/verify',
  operationId: 'Post_Face_Match_Aws',
  summary: 'Face Match',
  priority: 1,
};

const stripDataPrefix = (dataUrl: string | null) =>
  (dataUrl ?? '').replace(/^data:[^;]+;base64,/, '');

/**
 * Runs one Face Match step in a live session. The applicant captures ONE image —
 * a LIVE camera selfie (getUserMedia, capture/retake) → source_image. The reference
 * (target_image) is filled by the backend from the earlier Aadhaar step's photo, so
 * there's no upload. On a technical failure the runner advances to the next enabled
 * provider (visible priority fallback), same as the other runners.
 */
export function FaceStepRunner({
  session,
  step,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
}) {
  const { mutate, isPending } = useVerifyStep(session.id);

  const bindings = useMemo(() => {
    const b = step.apiBindings.slice().sort((x, y) => x.priority - y.priority);
    return b.length > 0 ? b : [DEFAULT_BINDING];
  }, [step.apiBindings]);

  // Two phases, matching the builder preview: read the guidance, then the camera opens.
  const [phase, setPhase] = useState<'prepare' | 'capture'>('prepare');
  const [activeIdx, setActiveIdx] = useState(0);
  const [fallbackNote, setFallbackNote] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  // Live selfie via the front camera — only on the capture screen, while the step is
  // active and not yet terminal (so the camera releases on failure/expired screens).
  // Gating on the phase means the permission prompt and the camera light arrive when
  // the applicant asks to be photographed, not while they are still reading the
  // guidance — and the stream is opened with the <video> already on screen.
  const cameraActive =
    phase === 'capture' && !expired && !session.isExpired && step.status !== 'Failed';
  const {
    videoRef,
    captured: selfieDataUrl,
    error: cameraError,
    capture,
    retake,
    quality,
    blinks,
    blinkTimedOut,
    blinksRequired,
    blinkAvailable,
    blinkWarming,
    // Blink-to-capture here too. Face Match does not NEED liveness proof — it needs one good
    // frame — but it puts the applicant in the same circle as the Liveness step, and two
    // identical screens that behave differently read as a bug. It also costs nothing extra:
    // the landmarker is cached across both steps.
  } = useCameraCapture(cameraActive, { blink: true });

  // Ring state: neutral until a still is taken or a verdict exists. A captured still is
  // never red — the frame passed the check to be taken at all, and re-judging a frozen
  // image would flicker on the retake screen.
  // Neutral — the designer's gradient — while the model loads. Green would claim the
  // frame had been checked when nothing has looked at it yet, and it would sit beside a
  // disabled Capture button saying the opposite.
  const ringState = selfieDataUrl || blinkWarming ? 'neutral' : quality.ok ? 'ok' : 'bad';

  // The same hint ladder as Liveness, in the order things block the applicant: framing
  // first, then blink progress, then the manual button once blinking is not an option.
  const captureHint = selfieDataUrl
    ? null
    : blinkWarming
      ? 'Getting ready…'
      : !quality.ok
        ? quality.message
        : blinkTimedOut || !blinkAvailable
          ? 'Tap Capture when you are ready'
          : `Blink slowly — ${blinks} of ${blinksRequired}`;

  const active = bindings[activeIdx] ?? bindings[0] ?? DEFAULT_BINDING;
  // Only the live selfie is required. The reference (target) photo is filled by the
  // backend from an earlier Aadhaar step's photo; the upload is an optional override.
  const ready = selfieDataUrl !== null;

  if (expired || session.isExpired) {
    return <SessionExpiredScreen />;
  }

  // Business reject (step Failed) → terminal rejection screen (low match, or a
  // Reject risk-rule / condition). A reject ends the session — no retry.
  if (step.status === 'Failed') {
    return <FailureScreen reason={extractReason(step.resultJson)} />;
  }

  function buildInput(): Record<string, unknown> {
    // Applicant data only — the backend verifier's NormalizeInput injects the
    // consent trio + similarity_threshold. source_image = the LIVE selfie;
    // target_image is left EMPTY and the backend fills it from the earlier Aadhaar
    // step's photo. Raw base64 (no "data:" prefix).
    return {
      source_image: stripDataPrefix(selfieDataUrl),
      target_image: '',
    };
  }

  function handleSubmit() {
    if (!ready) return;
    submitInput(buildInput());
  }

  // DEV: one-click scenario — submits a `test_case` (backend
  // FaceLivenessMockResponses returns the canned result) through the SAME flow.
  // Stripped from production builds.
  function runTestCase(testCase: string) {
    submitInput({ source_image: '', target_image: '', test_case: testCase });
  }

  function submitInput(input: Record<string, unknown>) {
    mutate(
      {
        stepId: step.id,
        input,
        catalogueEntryId: active.catalogueEntryId || undefined,
      },
      {
        onSuccess: (data) => {
          const updated = data.steps.find((s) => s.id === step.id);
          // Provisional shared-link mode: fresh step ids won't match — the entry
          // screen navigates to the real run on success. Nothing to do here.
          if (!updated) return;
          const status = updated?.status;
          if (status === 'Passed' || status === 'Review' || status === 'Failed') {
            // Final verdict — the parent advances / shows the failure screen.
            return;
          }
          // status Pending → technical provider failure → visible fallback.
          const nextBinding = bindings[activeIdx + 1];
          if (nextBinding) {
            // Same reason as the Bank runner: `summary` is the provider's internal
            // name, which the applicant can neither use nor act on.
            setFallbackNote(tryAnotherMethodNote());
            setActiveIdx(activeIdx + 1);
          } else {
            toast.error(CHECK_FAILED_TOAST);
            setFallbackNote(RETRY_EXHAUSTED_NOTE);
            setActiveIdx(0);
          }
        },
        onError: (error) => {
          if (isSessionExpiredError(error)) {
            setExpired(true);
            return;
          }
          toast.error('Could not reach the server. Please try again.');
        },
      },
    );
  }

  return (
    // `flex-1` so the runner fills the card: the action buttons below carry `mt-auto`
    // and therefore sit at the BOTTOM on every step, in the same place, with the
    // "Secured by" line beneath them. Without it each screen put its button wherever
    // its content happened to end, so the button jumped as the applicant advanced.
    <div className="flex flex-1 flex-col gap-4">
      {/* DEV-ONLY: one-click face scenarios (backend FaceLivenessMockResponses)
          to exercise the step's Risk / Advanced Rules. Stripped from prod. */}
      {import.meta.env.DEV && <FaceDevSimulator onRun={runTestCase} isPending={isPending} />}

      {fallbackNote && (
        <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]">
          {fallbackNote}
        </div>
      )}

      {/* PREPARE phase — the instructions screen. The builder's preview has always
          shown this before the camera, but the real runner jumped straight to capture,
          so an applicant met an open camera with no idea what was expected. Continue is
          the only way forward, which also means the camera does not start until the
          applicant has actually read the guidance. */}
      {phase === 'prepare' ? (
        <>
          <FaceMatchPrepareScreen />
          <button
            type="button"
            onClick={() => setPhase('capture')}
            style={{
              backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
              color: 'var(--brand-btn-text, #ffffff)',
              borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
              borderRadius: 'var(--brand-btn-radius, 0.5rem)',
            }}
            className="mt-auto flex w-full items-center justify-center gap-2 border py-3 text-[length:var(--text-16)] font-medium"
          >
            Continue
          </button>
        </>
      ) : (
        <>
          <FaceMatchScreen
            ringState={ringState}
            hint={captureHint}
            selfie={
              selfieDataUrl ? (
                // Mirror the captured still so it matches the live (mirrored) preview.
                <img
                  src={selfieDataUrl}
                  alt="Live selfie"
                  className="h-full w-full -scale-x-100 object-cover"
                />
              ) : cameraError ? (
                <span className="px-2 text-center text-[10px] leading-tight text-[#DC2626]">
                  {cameraError}
                </span>
              ) : (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="h-full w-full -scale-x-100 object-cover"
                />
              )
            }
          />

          {/* Live selfie capture/retake — the only action; the reference is auto-sourced. */}
          <button
            type="button"
            onClick={selfieDataUrl ? retake : capture}
            // Blocked while the model is still loading: for those few seconds nothing is
            // checking the frame, and that is exactly the frame that must not be taken.
            disabled={!!cameraError || (!selfieDataUrl && (!quality.ok || blinkWarming))}
            title={!selfieDataUrl && quality.message ? quality.message : undefined}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary py-2.5 text-sm font-medium text-primary transition-colors hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {selfieDataUrl ? <RefreshCw size={16} /> : <Camera size={16} />}
            {selfieDataUrl ? 'Retake Selfie' : 'Capture Selfie'}
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={isPending || !ready}
            style={{
              backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
              color: 'var(--brand-btn-text, #ffffff)',
              borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
              borderRadius: 'var(--brand-btn-radius, 0.75rem)',
            }}
            className="flex w-full items-center justify-center gap-2 border py-3 text-[15px] font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isPending && <Loader2 size={15} className="animate-spin" />}
            Continue
          </button>
        </>
      )}
    </div>
  );
}

// Shown when a Face Match step is rejected (business Failed) — e.g. a Reject
// risk-rule fired. Terminal — the session is Rejected.
function FailureScreen({ reason }: { reason?: string | null }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
      <div className="mb-6 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#DC2626]">
        <X size={60} strokeWidth={3} color="white" />
      </div>
      <h2 className="mb-2 text-[length:var(--text-20)] font-semibold leading-tight text-[#DC2626]">
        Face verification failed
      </h2>
      <p className="text-[length:var(--text-14)] text-[#666666]">
        {reason ?? 'Your selfie could not be verified. This session has been rejected.'}
      </p>
    </div>
  );
}
